// ONE SETTLE-LATER DEAL ON THE PARTY PAGE'S "OUTSTANDING WORK" (Astra's task row): the deal, its legs in the desk's words,
// and "Settle" — which opens Record money PREFILLED for the open leg (party, direction, asset, the leg's open figure): the
// party box is the unit, settling is ordinary money in / out against it, and the sweep closes the leg (the same model as
// the Settle later screen; no new posting). Mark settled stays beneath as the quiet, guarded override (SettleButton: its
// legs_open refusal, Mark settled anyway with its confirm).
import { Link } from 'react-router-dom';
import { ArrowLeftRight } from 'lucide-react';
import type { SettlementParty } from '../../../lib/api';
import { SettleButton, legWords } from '../../SettleButton';
import { displayAsset, fmtAmount } from '../../../lib/format';
import { shortReference } from '../../../lib/naming';

type Deal = SettlementParty['deals'][number];

/** The open leg the Settle door prefills: what the customer still pays (in) first, else what the desk still pays (out). */
export function settleHref(partySlug: string, d: Deal): string {
  const base = `/record?party=${encodeURIComponent(partySlug)}`;
  const legs = d.legs;
  if (!legs) return base;
  const leg = !legs.take.settled && legs.take.open > 0 ? { l: legs.take, dir: 'in' } : !legs.give.settled && legs.give.open > 0 ? { l: legs.give, dir: 'out' } : null;
  if (!leg) return base;
  return `${base}&dir=${leg.dir}&asset=${encodeURIComponent(leg.l.asset)}&amount=${leg.l.open}`;
}

export function OutstandingRow({ deal, partySlug }: { deal: Deal; partySlug: string }) {
  return (
    <div className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-start gap-3 border-b border-stroke-soft py-4 first:pt-0 last:border-b-0 last:pb-0">
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-warn-bg text-warn" aria-hidden><ArrowLeftRight className="h-4 w-4" /></span>
      <div className="min-w-0">
        <div className="truncate text-body font-medium" title={deal.deal_id}><span className="font-mono">{shortReference(deal.deal_id)}</span> · Settle later</div>
        {deal.legs && (
          // Each leg one unbreakable group, the figure before its unit ("52,728 AED to receive") — a phone line never strands a
          // unit from its figure (Astra, 1 Nov 2026). A closed leg keeps the shared words for how it closed.
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-body">
            {(['take', 'give'] as const).map((side) => {
              const l = deal.legs![side];
              return (
                <span key={side} className={`whitespace-nowrap ${l.settled ? 'text-pos' : 'text-warn'}`}>
                  {l.settled ? <span className="text-meta">{legWords(l, side)}</span> : <><span className="font-mono tabular">{fmtAmount(l.open, l.asset)}</span> <span className="text-meta">{displayAsset(l.asset)} {side === 'take' ? 'to receive' : 'to pay'}</span></>}
                </span>
              );
            })}
          </div>
        )}
      </div>
      <Link to={settleHref(partySlug, deal)} className="btn-secondary min-h-11 px-4 text-body lg:min-h-10">Settle</Link>
      <div className="col-span-2 col-start-2 flex justify-end">
        <SettleButton dealId={deal.deal_id} partySlug={partySlug} panelClassName="w-full" />
      </div>
    </div>
  );
}
